import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  FaBrain,
  FaRobot,
  FaSmile,
  FaComments,
  FaQuestionCircle,
  FaPlayCircle,
  FaChartLine,
  FaCheck,
  FaBars,
  FaTimes,
  FaArrowUp,
  FaAngleDown,
  FaArrowRight,
  FaLinkedin,
  FaRocket,
  FaBriefcase,
  FaFileAlt,
  FaCrown,
  FaCoins,
  FaUsers,
} from 'react-icons/fa';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { enhancedPlans } from '../features/payments/enhanced-pricing-plans.type';
import SEO from '../features/seo/seo.feature';
import PricingCalculator from '../components/PricingCalculator';

const TestimonialPrevArrow = (props: { onClick?: React.MouseEventHandler<HTMLDivElement> }) => {
  const { onClick } = props;
  return (
    <div
      className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-5 z-10 cursor-pointer bg-white p-3 rounded-full shadow-md hover:bg-gray-50 transition"
      onClick={onClick}
    >
      <FaAngleDown className="text-indigo-600 transform rotate-90" />
    </div>
  );
};

const TestimonialNextArrow = (props: { onClick?: React.MouseEventHandler<HTMLDivElement> }) => {
  const { onClick } = props;
  return (
    <div
      className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-5 z-10 cursor-pointer bg-white p-3 rounded-full shadow-md hover:bg-gray-50 transition"
      onClick={onClick}
    >
      <FaAngleDown className="text-indigo-600 transform -rotate-90" />
    </div>
  );
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [, setVideoLoaded] = useState(false);
  const [pricingMode, setPricingMode] = useState<'subscription' | 'pay-as-you-go'>('subscription');
  const [showCalculator, setShowCalculator] = useState(false);
  const link = 'https://res.cloudinary.com/appdata-rock/video/upload/v1739013345/8048249-hd_1920_1080_25fps_yrv74i.mp4';

  // Pay-as-you-go pricing structure
  const payAsYouGoPricing = {
    interviewSession: { credits: 5, price: 0.50, description: 'AI-powered interview practice session' },
    jobAnalysis: { credits: 3, price: 0.30, description: 'Smart job description analysis' },
    cvGeneration: { credits: 8, price: 0.80, description: 'ATS-optimized CV generation' },
    companyResearch: { credits: 2, price: 0.20, description: 'Company insights and research' },
    skillsGapAnalysis: { credits: 4, price: 0.40, description: 'Personalized skills assessment' }
  };
  // Safe analytics call - only runs once and handles errors
  useEffect(() => {
    try {
      // Only call analytics if it's available and not a bot
      if (typeof window !== 'undefined' && 'navigator' in window && !navigator.userAgent.includes('bot')) {
        import('../features/firebase/firebase-config').then(({ analytics }) => {
          analytics.logEvent('page_view', {
            page_title: 'landing'
          });
        }).catch(() => {
          // Silently fail if analytics not available
        });
      }
    } catch (error) {
      // Silently fail for bots/crawlers
    }
  }, []);

  // Define structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "PrepBestie - All-in-One AI Career Preparation & Communication Platform",
    "url": "https://prepbestie.com",
    "logo": "https://prepbestie.com/prepbestie.png",
    "description": "All-in-one AI career platform combining job analysis, CV generation, interview practice, and progress tracking for complete career success and communication skills development.",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web, iOS, Android",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "author": {
      "@type": "Organization",
      "name": "PrepBestie",
      "url": "https://prepbestie.com",
      "logo": "https://prepbestie.com/prepbestie.png"
    },
    "keywords": "all-in-one career platform, AI career preparation, job analysis, CV generator, interview practice, communication skills, career success"
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      setShowBackToTop(scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Video loading handler to prevent blocking
  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleVideoError = () => {
    // Fallback - hide video if it fails to load
    setVideoLoaded(false);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 600,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  // Framer Motion variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="bg-gray-50 font-sans">
      <SEO
        title='All-in-One AI Career Preparation & Communication Platform | PrepBestie'
        description='Complete AI career platform combining job analysis, CV generation, interview practice, and progress tracking. All-in-one solution for career preparation and communication skills development.'
        name='PrepBestie'
        type='website'
        structuredData={structuredData}
        keywords={
          [
            'All-in-one career platform',
            'AI career preparation',
            'PrepBestie',
            'Job analysis',
            'CV generator',
            'Interview practice',
            'Communication skills',
            'Career success platform',
            'Professional development',
            'AI career coaching',
            'Complete career solution',
            'Career preparation platform',
            'AI-powered career tools',
            'Job preparation',
            'Career advancement',
            'Professional communication',
            'Career analytics'
          ]
        }
      />
      {/* Modern Navbar */}
      <nav
        className={`fixed w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-lg' : 'bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between">
          <motion.div
            className="flex items-center gap-2 text-2xl font-bold"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className={`${isScrolled ? 'text-indigo-600' : 'text-white'}`}>
              Prep<span className={`${isScrolled ? 'text-indigo-400' : 'text-indigo-200'}`}>Bestie</span>
            </span>
          </motion.div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <motion.button
              onClick={() => navigate('/login')}
              className={`font-semibold transition-colors ${isScrolled ? 'text-indigo-600 hover:text-indigo-800' : 'text-white/90 hover:text-white'}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              Login
            </motion.button>
            <motion.button
              onClick={() => navigate('/create-account')}
              className={`px-4 py-2 rounded-full font-semibold shadow-md transition-all transform hover:scale-105 ${isScrolled
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-white text-indigo-600 hover:bg-gray-100'
                }`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              whileHover={{ y: -2 }}
            >
              Sign Up
            </motion.button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden">
            <motion.button
              aria-label="Toggle navigation menu"
              onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
              className={`focus:outline-none ${isScrolled ? 'text-indigo-600' : 'text-white'}`}
              whileTap={{ scale: 0.9 }}
            >
              {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-white shadow-lg"
            >
              <div className="px-4 py-6 flex flex-col items-center space-y-4">
                <motion.button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/login');
                  }}
                  className="text-indigo-600 font-semibold hover:text-indigo-800 transition w-full py-2"
                  whileTap={{ scale: 0.95 }}
                >
                  Login
                </motion.button>
                <motion.button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    navigate('/create-account');
                  }}
                  className="bg-indigo-600 text-white w-full px-6 py-3 rounded-full font-semibold hover:bg-indigo-700 transition shadow-md"
                  whileTap={{ scale: 0.95 }}
                >
                  Sign Up
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Modern Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden">
        {/* Background with fallback for bots/slow connections */}
        <div className="absolute inset-0">
          {/* Fallback gradient background - always visible for crawlers */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-indigo-800 to-purple-900"></div>

          {/* Video - only loads for users, not bots */}
          {typeof window !== 'undefined' && !navigator.userAgent.includes('bot') && (
            <video
              autoPlay
              muted
              loop
              className="w-full h-full object-cover"
              playsInline
              onLoadedData={handleVideoLoad}
              onError={handleVideoError}
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'%3E%3Crect width='100%25' height='100%25' fill='%234F46E5'/%3E%3C/svg%3E"
            >
              <source src={link} type="video/mp4" />
            </video>
          )}

          <div className="absolute inset-0 bg-gradient-to-b from-indigo-900/80 via-indigo-800/60 to-indigo-900/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-200"
              variants={fadeIn}
            >
              All-in-One AI Practice for Interviews, Pitches & Public Speaking
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl md:text-2xl mb-10 text-white/90 max-w-3xl mx-auto font-light"
              variants={fadeIn}
            >
              Master every opportunity with PrepBestie: AI-powered job analysis, simulator-friendly interview practice, real-time feedback, CV generation, and progress tracking—all in one seamless platform.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6"
              variants={fadeIn}
            >
              <motion.button
                onClick={() => navigate('/create-account')}
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition font-semibold flex items-center justify-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Get Started Free
                <FaArrowRight className="transition-transform group-hover:translate-x-1" />
              </motion.button>
              {/* <motion.button
                onClick={() => setDemoModalOpen(true)}
                className="mt-4 sm:mt-0 bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full hover:bg-white/30 transition font-semibold flex items-center justify-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaPlayCircle /> Watch Demo
              </motion.button> */}
            </motion.div>
            {/* <motion.div
              className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 1 }}
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-white/50 text-2xl cursor-pointer"
                onClick={() => {
                  const howItWorksSection = document.getElementById('how-it-works');
                  if (howItWorksSection) {
                    howItWorksSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <FaAngleDown />
              </motion.div>
            </motion.div> */}
            <p className="text-white/90 text-sm mt-3 text-center">
              No credit card required to start
            </p>
          </motion.div>
        </div>
      </section>

      {/* Demo Modal - Improved version */}
      <AnimatePresence>
        {demoModalOpen && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDemoModalOpen(false)}
            ></motion.div>
            <motion.div
              className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full relative z-10"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", bounce: 0.25 }}
            >
              <div className="flex justify-end p-4 absolute top-0 right-0 z-10">
                <button
                  onClick={() => setDemoModalOpen(false)}
                  className="text-gray-500 hover:text-gray-900 bg-white/80 backdrop-blur-sm p-2 rounded-full"
                >
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="p-0">
                <div className="relative pb-[56.25%]">
                  <iframe
                    title="Product Demo"
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                    frameBorder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ------------------------- */}
      {/* HOW IT WORKS SECTION - Modernized */}
      {/* ------------------------- */}
      <section id="how-it-works" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-indigo-400">
                How It Works
              </span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg md:text-xl">
              From job analysis to CV generation, personalized practice sessions, and progress tracking—discover how our all-in-one platform transforms your communication skills and career prospects.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-6">
            {/* Step 1 - Job Analysis */}
            <motion.div
              className="relative overflow-hidden p-8 rounded-2xl shadow-lg group bg-gradient-to-br from-white to-gray-50 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-10 -top-10 w-24 h-24 bg-indigo-100 rounded-full opacity-70 group-hover:scale-125 transition-all duration-500"></div>
              <div className="text-indigo-500 text-5xl mb-6 relative z-10 group-hover:scale-110 transition-all duration-300">
                <FaBrain />
              </div>
              <h3 className="text-xl font-bold mb-3 relative z-10">1. Analyze Jobs</h3>
              <p className="text-gray-600 relative z-10">
                Paste any job description and get AI-powered insights on skills, requirements, and preparation strategies tailored to your profile.
              </p>
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-indigo-300 to-indigo-500 group-hover:w-full transition-all duration-500"></div>
            </motion.div>

            {/* Step 2 - Profile & CV */}
            <motion.div
              className="relative overflow-hidden p-8 rounded-2xl shadow-lg group bg-gradient-to-br from-white to-gray-50 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-10 -top-10 w-24 h-24 bg-purple-100 rounded-full opacity-70 group-hover:scale-125 transition-all duration-500"></div>
              <div className="text-purple-500 text-5xl mb-6 relative z-10 group-hover:scale-110 transition-all duration-300">
                <FaComments />
              </div>
              <h3 className="text-xl font-bold mb-3 relative z-10">2. Build Profile & CV</h3>
              <p className="text-gray-600 relative z-10">
                Complete your profile and generate optimized CVs with AI assistance, keyword optimization, and ATS-friendly formatting.
              </p>
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-purple-300 to-purple-500 group-hover:w-full transition-all duration-500"></div>
            </motion.div>

            {/* Step 3 - Personalized Practice */}
            <motion.div
              className="relative overflow-hidden p-8 rounded-2xl shadow-lg group bg-gradient-to-br from-white to-gray-50 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-10 -top-10 w-24 h-24 bg-yellow-100 rounded-full opacity-70 group-hover:scale-125 transition-all duration-500"></div>
              <div className="text-yellow-500 text-5xl mb-6 relative z-10 group-hover:scale-110 transition-all duration-300">
                <FaQuestionCircle />
              </div>
              <h3 className="text-xl font-bold mb-3 relative z-10">3. Get Tailored Questions</h3>
              <p className="text-gray-600 relative z-10">
                Receive simulator-friendly interview questions designed for verbal practice—no coding or IDE required, just pure communication focus.
              </p>
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-yellow-300 to-yellow-500 group-hover:w-full transition-all duration-500"></div>
            </motion.div>

            {/* Step 4 - Practice & Record */}
            <motion.div
              className="relative overflow-hidden p-8 rounded-2xl shadow-lg group bg-gradient-to-br from-white to-gray-50 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-10 -top-10 w-24 h-24 bg-blue-100 rounded-full opacity-70 group-hover:scale-125 transition-all duration-500"></div>
              <div className="text-blue-500 text-5xl mb-6 relative z-10 group-hover:scale-110 transition-all duration-300">
                <FaPlayCircle />
              </div>
              <h3 className="text-xl font-bold mb-3 relative z-10">4. Practice Speaking</h3>
              <p className="text-gray-600 relative z-10">
                Record your responses in realistic interview scenarios with AI interviewer voices and real-time guidance.
              </p>
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-blue-300 to-blue-500 group-hover:w-full transition-all duration-500"></div>
            </motion.div>

            {/* Step 5 - AI Analysis */}
            <motion.div
              className="relative overflow-hidden p-8 rounded-2xl shadow-lg group bg-gradient-to-br from-white to-gray-50 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-10 -top-10 w-24 h-24 bg-green-100 rounded-full opacity-70 group-hover:scale-125 transition-all duration-500"></div>
              <div className="text-green-500 text-5xl mb-6 relative z-10 group-hover:scale-110 transition-all duration-300">
                <FaRobot />
              </div>
              <h3 className="text-xl font-bold mb-3 relative z-10">5. Get AI Feedback</h3>
              <p className="text-gray-600 relative z-10">
                Receive comprehensive analysis on your delivery, emotion, speech patterns, and content with actionable improvement suggestions.
              </p>
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-green-300 to-green-500 group-hover:w-full transition-all duration-500"></div>
            </motion.div>

            {/* Step 6 - Track Progress */}
            <motion.div
              className="relative overflow-hidden p-8 rounded-2xl shadow-lg group bg-gradient-to-br from-white to-gray-50 border border-gray-100"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              whileHover={{ y: -5 }}
            >
              <div className="absolute -right-10 -top-10 w-24 h-24 bg-orange-100 rounded-full opacity-70 group-hover:scale-125 transition-all duration-500"></div>
              <div className="text-orange-500 text-5xl mb-6 relative z-10 group-hover:scale-110 transition-all duration-300">
                <FaChartLine />
              </div>
              <h3 className="text-xl font-bold mb-3 relative z-10">6. Track Progress</h3>
              <p className="text-gray-600 relative z-10">
                Monitor your improvement with detailed analytics, profile completeness tracking, and visual progress indicators across all sessions.
              </p>
              <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-orange-300 to-orange-500 group-hover:w-full transition-all duration-500"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ------------------------- */}
      {/* AI ADVANTAGE SECTION - Modernized */}
      {/* ------------------------- */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 to-purple-800"></div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-80 h-80 bg-indigo-600 rounded-full opacity-20 animate-pulse" style={{ animationDuration: '8s' }}></div>
          <div className="absolute top-40 -right-20 w-60 h-60 bg-purple-500 rounded-full opacity-20 animate-pulse" style={{ animationDuration: '10s' }}></div>
          <div className="absolute bottom-10 left-1/4 w-40 h-40 bg-indigo-400 rounded-full opacity-20 animate-pulse" style={{ animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-center relative z-10">
          <motion.div
            className="lg:w-1/2 mb-12 lg:mb-0 pr-0 lg:pr-12"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="relative">
              <div className="absolute -top-10 -left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-50"></div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white relative">
                Our AI <span className="text-yellow-300">Advantage</span>
              </h2>
            </div>
            <p className="text-lg text-white/90 mb-8">
              Leveraging advanced machine learning, our platform dives deep into your responses, offering insights on your speech patterns, emotions, and overall delivery. Enhance your performance with data-driven feedback that's tailored just for you.
            </p>
            {/* <motion.button
              onClick={() => navigate('/demo')}
              className="bg-white text-indigo-700 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition shadow-lg flex items-center gap-2 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Learn More
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </motion.button> */}
          </motion.div>
          <motion.div
            className="lg:w-1/2 flex justify-center relative"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/30 to-purple-500/30 rounded-full blur-3xl"></div>
            <motion.div
              animate={{
                y: [0, 15, 0],
                rotateZ: [0, 5, 0, -5, 0]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative z-10"
            >
              <DotLottieReact
                src="https://lottie.host/d6444ff4-e822-41c1-a05f-d5ebc9d4dd15/5LJoBkmhiT.lottie"
                loop
                autoplay
                className="w-80 h-80"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------- */}
      {/* FEATURES SECTION - Modernized */}
      {/* ------------------------- */}
      <section id="features" className="py-24 bg-gray-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute right-0 top-0 h-full text-indigo-100 w-1/2 transform translate-x-1/2" fill="currentColor" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon points="0,0 100,0 50,100 0,100" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              <span className="relative">
                Powerful Features
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full"></div>
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Everything you need to master your communication skills in one intuitive platform.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10">
            {/* Feature 1 - AI-Powered Job Analysis */}
            <motion.div
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-indigo-100 rounded-full scale-150 opacity-50 animate-pulse" style={{ animationDuration: '3s' }}></div>
                  <div className="relative bg-gradient-to-br from-indigo-500 to-indigo-700 text-white text-4xl rounded-full p-4">
                    <FaBrain />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center text-gray-800">Smart Job Analysis</h3>
              <p className="text-gray-600 text-center mb-4 flex-grow">
                Analyze any job description with AI-powered insights, skill requirements, and personalized preparation recommendations tailored to your profile.
              </p>
              <div className="h-1 w-20 bg-indigo-500 mx-auto rounded-full"></div>
            </motion.div>

            {/* Feature 2 - CV Generation & Optimization */}
            <motion.div
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-100 rounded-full scale-150 opacity-50 animate-pulse" style={{ animationDuration: '3s' }}></div>
                  <div className="relative bg-gradient-to-br from-purple-500 to-purple-700 text-white text-4xl rounded-full p-4">
                    <FaComments />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center text-gray-800">AI CV Generator</h3>
              <p className="text-gray-600 text-center mb-4 flex-grow">
                Generate and optimize professional CVs with AI assistance, keyword optimization, and ATS-friendly formatting for every job application.
              </p>
              <div className="h-1 w-20 bg-purple-500 mx-auto rounded-full"></div>
            </motion.div>

            {/* Feature 3 - Simulator-Friendly Practice */}
            <motion.div
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-blue-100 rounded-full scale-150 opacity-50 animate-pulse" style={{ animationDuration: '3s' }}></div>
                  <div className="relative bg-gradient-to-br from-blue-500 to-blue-700 text-white text-4xl rounded-full p-4">
                    <FaPlayCircle />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center text-gray-800">Simulator-Friendly Questions</h3>
              <p className="text-gray-600 text-center mb-4 flex-grow">
                Practice with interview questions designed for verbal responses—no coding or IDE required. Focus on communication, not technical tools.
              </p>
              <div className="h-1 w-20 bg-blue-500 mx-auto rounded-full"></div>
            </motion.div>

            {/* Feature 4 - Real-Time Feedback & Analysis */}
            <motion.div
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-green-100 rounded-full scale-150 opacity-50 animate-pulse" style={{ animationDuration: '3s' }}></div>
                  <div className="relative bg-gradient-to-br from-green-500 to-green-700 text-white text-4xl rounded-full p-4">
                    <FaRobot />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center text-gray-800">Real-Time AI Feedback</h3>
              <p className="text-gray-600 text-center mb-4 flex-grow">
                Get immediate, actionable insights on your delivery, emotion analysis, and speech patterns to improve your performance on the spot.
              </p>
              <div className="h-1 w-20 bg-green-500 mx-auto rounded-full"></div>
            </motion.div>

            {/* Feature 5 - Progress Tracking & Analytics */}
            <motion.div
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-yellow-100 rounded-full scale-150 opacity-50 animate-pulse" style={{ animationDuration: '3s' }}></div>
                  <div className="relative bg-gradient-to-br from-yellow-500 to-yellow-600 text-white text-4xl rounded-full p-4">
                    <FaChartLine />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center text-gray-800">Progress Tracking</h3>
              <p className="text-gray-600 text-center mb-4 flex-grow">
                Monitor your improvements with detailed analytics, profile completeness tracking, and visual progress indicators across all practice sessions.
              </p>
              <div className="h-1 w-20 bg-yellow-500 mx-auto rounded-full"></div>
            </motion.div>

            {/* Feature 6 - Seamless Integrations */}
            <motion.div
              className="p-8 bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full flex flex-col"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="flex justify-center items-center mb-6">
                <div className="relative">
                  <div className="absolute inset-0 bg-red-100 rounded-full scale-150 opacity-50 animate-pulse" style={{ animationDuration: '3s' }}></div>
                  <div className="relative bg-gradient-to-br from-red-500 to-red-700 text-white text-4xl rounded-full p-4">
                    <FaSmile />
                  </div>
                </div>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-center text-gray-800">Secure Platform</h3>
              <p className="text-gray-600 text-center mb-4 flex-grow">
                Built with enterprise-grade security, seamless payment processing, and best-in-class integrations for a smooth, trustworthy experience.
              </p>
              <div className="h-1 w-20 bg-red-500 mx-auto rounded-full"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY PREPBESTIE SECTION */}
      <section className="py-20 bg-gradient-to-br from-indigo-900 to-purple-900 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-indigo-600 rounded-full opacity-20 animate-pulse" style={{ animationDuration: '8s' }}></div>
          <div className="absolute top-20 -right-20 w-60 h-60 bg-purple-500 rounded-full opacity-20 animate-pulse" style={{ animationDuration: '10s' }}></div>
          <div className="absolute bottom-10 left-1/4 w-40 h-40 bg-indigo-400 rounded-full opacity-20 animate-pulse" style={{ animationDuration: '6s' }}></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.h2
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            Why Choose <span className="text-yellow-300">PrepBestie?</span>
          </motion.h2>
          <motion.p
            className="text-lg text-white/90 mb-10 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
          >
            We're not just another practice platform. PrepBestie is the complete solution for modern professionals who want to excel in every speaking opportunity.
          </motion.p>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 text-lg max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
            viewport={{ once: true }}
          >
            <motion.div variants={fadeIn} className="flex items-center gap-4 justify-center md:justify-start">
              <div className="bg-yellow-300 rounded-full p-3">
                <FaRobot className="text-indigo-900 text-xl" />
              </div>
              <span>True AI-powered practice—no generic templates, always personalized</span>
            </motion.div>
            <motion.div variants={fadeIn} className="flex items-center gap-4 justify-center md:justify-start">
              <div className="bg-yellow-300 rounded-full p-3">
                <FaBrain className="text-indigo-900 text-xl" />
              </div>
              <span>Deep job analysis and tailored CV generation for every opportunity</span>
            </motion.div>
            <motion.div variants={fadeIn} className="flex items-center gap-4 justify-center md:justify-start">
              <div className="bg-yellow-300 rounded-full p-3">
                <FaChartLine className="text-indigo-900 text-xl" />
              </div>
              <span>Visual progress tracking and actionable analytics</span>
            </motion.div>
            <motion.div variants={fadeIn} className="flex items-center gap-4 justify-center md:justify-start">
              <div className="bg-yellow-300 rounded-full p-3">
                <FaPlayCircle className="text-indigo-900 text-xl" />
              </div>
              <span>Simulator-friendly: practice speaking, not coding</span>
            </motion.div>
            <motion.div variants={fadeIn} className="flex items-center gap-4 justify-center md:justify-start">
              <div className="bg-yellow-300 rounded-full p-3">
                <FaCheck className="text-indigo-900 text-xl" />
              </div>
              <span>Enterprise-grade security, privacy-first, trusted by thousands</span>
            </motion.div>
            <motion.div variants={fadeIn} className="flex items-center gap-4 justify-center md:justify-start">
              <div className="bg-yellow-300 rounded-full p-3">
                <FaSmile className="text-indigo-900 text-xl" />
              </div>
              <span>All-in-one platform: job analysis, practice, CVs, progress tracking</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------- */}
      {/* TESTIMONIALS SECTION - Modernized */}
      {/* ------------------------- */}
      <section id="testimonials" className="py-24 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -left-40 top-20 w-80 h-80 bg-indigo-50 rounded-full"></div>
          <div className="absolute -right-40 bottom-20 w-80 h-80 bg-indigo-50 rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                What Our Users Are Saying
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Join thousands of satisfied users who have transformed their communication skills with PrepBestie.
            </p>
          </motion.div>

          <div className="relative testimonial-container px-10">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              className="rounded-2xl overflow-hidden p-1 bg-gradient-to-r from-indigo-500 to-purple-500"
            >
              <div className="bg-white rounded-xl p-6">
                <Slider
                  {...sliderSettings}
                  className="testimonial-slider"
                  prevArrow={<TestimonialPrevArrow />}
                  nextArrow={<TestimonialNextArrow />}
                >
                  <div className="p-4">
                    <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-md h-full flex flex-col justify-between relative">
                      <div className="absolute -top-4 right-10 w-12 h-12 bg-indigo-500 flex items-center justify-center rounded-full text-white text-xl font-serif">
                        "
                      </div>
                      <p className="text-gray-600 mb-6 italic mt-4">
                        "PrepBestie's job analysis and CV generator completely transformed my interview prep. The AI insights and tailored questions helped me land my dream job after just two weeks of practice!"
                      </p>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4 text-indigo-600 font-bold">
                          S
                        </div>
                        <div>
                          <div className="font-bold text-indigo-700">Sarah Johnson</div>
                          <div className="text-sm text-gray-500">UX Designer, Google</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-md h-full flex flex-col justify-between relative">
                      <div className="absolute -top-4 right-10 w-12 h-12 bg-indigo-500 flex items-center justify-center rounded-full text-white text-xl font-serif">
                        "
                      </div>
                      <p className="text-gray-600 mb-6 italic mt-4">
                        "The real-time feedback and progress tracking are invaluable. I love how the profile completeness bar shows my improvement, and the simulator-friendly questions let me focus on communication skills. My confidence has grown exponentially!"
                      </p>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4 text-indigo-600 font-bold">
                          M
                        </div>
                        <div>
                          <div className="font-bold text-indigo-700">Mark Williams</div>
                          <div className="text-sm text-gray-500">MBA Graduate, Wharton</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-md h-full flex flex-col justify-between relative">
                      <div className="absolute -top-4 right-10 w-12 h-12 bg-indigo-500 flex items-center justify-center rounded-full text-white text-xl font-serif">
                        "
                      </div>
                      <p className="text-gray-600 mb-6 italic mt-4">
                        "Using PrepBestie for my public speaking engagements has boosted my confidence like never before. My presentations are now more engaging and impactful."
                      </p>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4 text-indigo-600 font-bold">
                          A
                        </div>
                        <div>
                          <div className="font-bold text-indigo-700">Aisha Patel</div>
                          <div className="text-sm text-gray-500">Public Speaker & TEDx Presenter</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-md h-full flex flex-col justify-between relative">
                      <div className="absolute -top-4 right-10 w-12 h-12 bg-indigo-500 flex items-center justify-center rounded-full text-white text-xl font-serif">
                        "
                      </div>
                      <p className="text-gray-600 mb-6 italic mt-4">
                        "The comprehensive analytics, job analysis insights, and CV optimization features helped me fine-tune my approach effortlessly. I've seen a 30% increase in my interview success rate since using PrepBestie's all-in-one platform."
                      </p>
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4 text-indigo-600 font-bold">
                          J
                        </div>
                        <div>
                          <div className="font-bold text-indigo-700">John Martinez</div>
                          <div className="text-sm text-gray-500">Sales Manager, Salesforce</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Slider>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ------------------------- */}
      {/* FAQ SECTION - Fix Animations */}
      {/* ------------------------- */}
      <section id="faq" className="py-24 bg-gray-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <svg className="absolute left-0 top-0 h-full text-white w-1/3 transform -translate-x-1/4" fill="currentColor" viewBox="0 0 100 100" preserveAspectRatio="none">
            <circle cx="50" cy="50" r="50" />
          </svg>
          <svg className="absolute right-0 bottom-0 h-full text-white w-1/3 transform translate-x-1/4" fill="currentColor" viewBox="0 0 100 100" preserveAspectRatio="none">
            <circle cx="50" cy="50" r="50" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              <span className="relative inline-block">
                Frequently Asked Questions
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
              </span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              Got questions? We've got answers to help you get started with PrepBestie.
            </p>
          </motion.div>

          <div className="space-y-6">
            {faqItems.map((faq, index) => (
              <FaqItem key={index} question={faq.question} answer={faq.answer} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SECTION - Fix spacing and overflow */}
      {/* ------------------------- */}
      <section id="pricing" className="py-24 bg-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -left-20 top-40 w-64 h-64 bg-yellow-100 rounded-full opacity-70"></div>
          <div className="absolute right-0 bottom-0 w-80 h-80 bg-indigo-50 rounded-full"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                Flexible Pricing Options
              </span>
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg mb-8">
              Choose between unlimited subscription plans or flexible pay-as-you-go credits. Perfect for both regular users and occasional job seekers.
            </p>
            
            {/* Pricing Mode Toggle */}
            <motion.div 
              className="flex items-center justify-center mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-100 rounded-full p-1 flex">
                <button
                  onClick={() => setPricingMode('subscription')}
                  className={`px-6 py-3 rounded-full transition-all duration-300 flex items-center ${
                    pricingMode === 'subscription' 
                      ? 'bg-white text-indigo-600 font-semibold shadow-lg' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <FaRocket className="mr-2" />
                  Subscriptions
                </button>
                <button
                  onClick={() => setPricingMode('pay-as-you-go')}
                  className={`px-6 py-3 rounded-full transition-all duration-300 flex items-center ${
                    pricingMode === 'pay-as-you-go' 
                      ? 'bg-white text-indigo-600 font-semibold shadow-lg' 
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <FaCoins className="mr-2" />
                  Pay-as-You-Go
                </button>
              </div>
            </motion.div>
          </motion.div>

          <AnimatePresence mode="wait">
            {pricingMode === 'subscription' ? (
              <motion.div
                key="subscription"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5 }}
                className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto px-4"
              >
                {enhancedPlans.map((plan, index) => (
                  <motion.div
                    key={index}
                    className={`relative group cursor-pointer ${
                      plan.name === 'Pro' ? 'lg:scale-110 z-10' : ''
                    }`}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: index * 0.2 }}
                    viewport={{ once: true, margin: "-100px" }}
                    whileHover={{ 
                      scale: plan.name === 'Pro' ? 1.05 : 1.02,
                      rotateY: 5,
                      transition: { duration: 0.3 }
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* Popular Badge */}
                    {plan.name === 'Pro' && (
                      <motion.div 
                        className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-20"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 px-6 py-2 rounded-full font-bold text-sm shadow-lg flex items-center">
                          <FaCrown className="mr-2" />
                          MOST POPULAR
                        </div>
                      </motion.div>
                    )}

                    <motion.div
                      className={`relative overflow-hidden rounded-3xl shadow-2xl transition-all duration-500 ${
                        plan.name === 'Pro' 
                          ? 'bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 border-2 border-yellow-400' 
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <div className={`p-8 relative z-10 ${plan.name === 'Pro' ? 'pt-16' : ''}`}>
                        {/* Plan Icon */}
                        <motion.div 
                          className="text-center mb-6"
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                        >
                          {plan.name === 'Free' && <FaRocket className="text-4xl mx-auto mb-4 text-blue-400" />}
                          {plan.name === 'Basic' && <FaBriefcase className="text-4xl mx-auto mb-4 text-green-400" />}
                          {plan.name === 'Pro' && <FaCrown className="text-4xl mx-auto mb-4 text-yellow-400" />}
                          {plan.name === 'Enterprise' && <FaUsers className="text-4xl mx-auto mb-4 text-purple-400" />}
                        </motion.div>

                        {/* Plan Name */}
                        <h3 className={`text-2xl font-bold text-center mb-4 ${plan.name === 'Pro' ? 'text-white' : 'text-gray-800'}`}>
                          {plan.name}
                        </h3>

                        {/* Price */}
                        <motion.div 
                          className="text-center mb-6"
                          whileHover={{ scale: 1.05 }}
                        >
                          <div className={`text-5xl font-bold mb-2 ${plan.name === 'Pro' ? 'text-white' : 'text-indigo-600'}`}>
                            {plan.amount === 0 ? 'Free' : `$${plan.amount}`}
                          </div>
                          {plan.amount > 0 && (
                            <div className={`text-lg ${plan.name === 'Pro' ? 'text-purple-200' : 'text-gray-600'}`}>
                              /month
                            </div>
                          )}
                        </motion.div>

                        {/* Description */}
                        <p className={`text-center mb-8 text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                          {plan.description}
                        </p>

                        {/* Features */}
                        <motion.div className="space-y-4 mb-8">
                          {/* Interview Features */}
                          <div>
                            <h4 className={`font-semibold mb-3 flex items-center ${plan.name === 'Pro' ? 'text-white' : 'text-gray-700'}`}>
                              <FaBriefcase className="mr-2 text-blue-400" />
                              Interview Practice
                            </h4>
                            <div className="space-y-2">
                              <div className={`flex items-center text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                                <FaCheck className={`mr-2 flex-shrink-0 ${plan.name === 'Pro' ? 'text-green-400' : 'text-green-500'}`} />
                                {plan.sessions === -1 ? 'Unlimited' : `${plan.sessions}`} sessions/month
                              </div>
                              <div className={`flex items-center text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                                <FaCheck className={`mr-2 flex-shrink-0 ${plan.name === 'Pro' ? 'text-green-400' : 'text-green-500'}`} />
                                {plan.recordingLength}min recordings
                              </div>
                              {plan.emailReminder && (
                                <div className={`flex items-center text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                                  <FaCheck className={`mr-2 flex-shrink-0 ${plan.name === 'Pro' ? 'text-green-400' : 'text-green-500'}`} />
                                  Email reminders
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Job Analysis Features */}
                          <div>
                            <h4 className={`font-semibold mb-3 flex items-center ${plan.name === 'Pro' ? 'text-white' : 'text-gray-700'}`}>
                              <FaFileAlt className="mr-2 text-green-400" />
                              Job Analysis
                            </h4>
                            <div className="space-y-2">
                              <div className={`flex items-center text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                                <FaCheck className={`mr-2 flex-shrink-0 ${plan.name === 'Pro' ? 'text-green-400' : 'text-green-500'}`} />
                                {plan.jobAnalysisSessions === -1 ? 'Unlimited' : `${plan.jobAnalysisSessions}`} analyses/month
                              </div>
                              <div className={`flex items-center text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                                <FaCheck className={`mr-2 flex-shrink-0 ${plan.name === 'Pro' ? 'text-green-400' : 'text-green-500'}`} />
                                {plan.cvGenerations === -1 ? 'Unlimited' : `${plan.cvGenerations}`} CV generations
                              </div>
                              {plan.companyResearch && (
                                <div className={`flex items-center text-sm ${plan.name === 'Pro' ? 'text-purple-100' : 'text-gray-600'}`}>
                                  <FaCheck className={`mr-2 flex-shrink-0 ${plan.name === 'Pro' ? 'text-green-400' : 'text-green-500'}`} />
                                  Company research
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>

                        {/* CTA Button */}
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <Link to="/create-account">
                            <button className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                              plan.name === 'Pro'
                                ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-500 shadow-lg'
                                : plan.amount === 0
                                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg'
                            }`}>
                              {plan.amount === 0 ? 'Start Free Today' : `Start ${plan.name} Plan`}
                            </button>
                          </Link>
                        </motion.div>

                        {plan.amount > 0 && (
                          <p className={`text-xs text-center mt-4 ${plan.name === 'Pro' ? 'text-purple-200' : 'text-gray-500'}`}>
                            14-day free trial • Cancel anytime
                          </p>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="pay-as-you-go"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5 }}
                className="max-w-7xl mx-auto px-4"
              >
                {/* Feature Pricing */}
                <div className="mb-16">
                  <h3 className="text-4xl font-bold text-center mb-8 text-gray-800">Pay Only for What You Use</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {Object.entries(payAsYouGoPricing).map(([key, feature], index) => (
                      <motion.div
                        key={key}
                        className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200"
                        whileHover={{ scale: 1.05, y: -5 }}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center">
                            <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center mr-4">
                              <FaCoins className="text-white text-xl" />
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-800 text-lg capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</h4>
                              <p className="text-sm text-gray-600">{feature.description}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Cost and Value Display */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="text-3xl font-bold text-indigo-600">
                              {feature.credits} credits
                            </div>
                            <div className="text-2xl text-gray-600 font-semibold">
                              ${feature.price}
                            </div>
                          </div>
                          
                          {/* Feature Value Breakdown */}
                          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4">
                            <div className="text-center">
                              <div className="text-sm text-gray-600 mb-2">What you get:</div>
                              <div className="text-2xl font-bold text-indigo-600 mb-1">
                                1 {key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}
                              </div>
                              <div className="text-sm text-gray-500">
                                per {feature.credits} credits
                              </div>
                            </div>
                          </div>
                          
                          {/* Additional Value Examples */}
                          <div className="text-center">
                            <div className="text-sm text-gray-600 mb-2">With 100 credits you can get:</div>
                            <div className="text-lg font-semibold text-gray-800">
                              {Math.floor(100 / feature.credits)} {key.replace(/([A-Z])/g, ' $1').trim().toLowerCase()}s
                            </div>
                            <div className="text-sm text-gray-500">
                              (${(Math.floor(100 / feature.credits) * feature.price).toFixed(2)} value)
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Usage Calculator CTA */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-3xl p-8 text-center">
                  <h3 className="text-2xl font-bold mb-4 text-gray-800">Not Sure How Much You'll Use?</h3>
                  <p className="text-gray-600 mb-6">Use our calculator to estimate your monthly usage and find the perfect credit package.</p>
                  <motion.button
                    onClick={() => setShowCalculator(true)}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg transition-all duration-300 flex items-center mx-auto"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaChartLine className="mr-2" />
                    Calculate My Usage
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>


          <motion.div
            className="text-center mt-20 max-w-2xl mx-auto bg-indigo-50 p-8 rounded-2xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <p className="text-gray-700 mb-4">
              <span className="font-semibold">Need a custom plan for your organization?</span> We offer special enterprise pricing with additional features for teams.
            </p>
            <a
              className="text-indigo-600 font-semibold hover:text-indigo-800 transition flex items-center justify-center mx-auto gap-2 group"
              href="mailto:info@prepbestie.com"
            >
              Contact our sales team
              <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ------------------------- */}
      {/* CALL-TO-ACTION - Modernized */}
      {/* ------------------------- */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-700"></div>

        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-indigo-900/30 to-transparent"></div>
          <div className="absolute top-1/3 left-0 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/3 right-10 w-60 h-60 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-10">
              <motion.div
                className="text-center lg:text-left"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
                  Ready to <span className="text-yellow-300">Elevate</span> Your Speaking?
                </h2>
                <p className="text-white/90 text-lg max-w-lg">
                  Sign up now to unlock personalized AI practice sessions and transform your communication skills.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <motion.button
                  onClick={() => navigate('/create-account')}
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-8 py-4 rounded-md shadow-lg hover:shadow-xl transition font-semibold flex items-center gap-3 group"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Start Practicing Now</span>
                  <div className="bg-yellow-500 rounded-full p-1 group-hover:translate-x-1 transition-transform">
                    <FaArrowRight />
                  </div>
                </motion.button>
                <p className="text-white/70 text-sm mt-3 text-center">
                  No credit card required to start
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------- */}
      {/* FOOTER - Modernized */}
      {/* ------------------------- */}
      <footer className="pt-20 pb-10 bg-gray-900 text-gray-300 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-900/20 rounded-full blur-3xl"></div>
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-900/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-16">
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 text-2xl font-bold text-white mb-4">
                <span>
                  Prep<span className="text-indigo-400">Bestie</span>
                </span>
              </div>
              <p className="text-gray-400 mb-4">
                Master interviews, pitches, and public speaking with AI-powered practice and personalized feedback.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.linkedin.com/company/prep-bestie/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-indigo-400 transition text-lg p-2 bg-gray-800/50 rounded-full"
                  aria-label="Linkedin"
                >
                  <FaLinkedin />
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Product</h3>
              <ul className="space-y-3">
                <li>
                  <a href="#features" className="text-gray-400 hover:text-indigo-400 transition">Features</a>
                </li>
                <li>
                  <a href="#pricing" className="text-gray-400 hover:text-indigo-400 transition">Pricing</a>
                </li>
                <li>
                  <a href="#testimonials" className="text-gray-400 hover:text-indigo-400 transition">Testimonials</a>
                </li>
                <li>
                  <a href="#faq" className="text-gray-400 hover:text-indigo-400 transition">FAQ</a>
                </li>
              </ul>
            </div>
            {/* 
            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Company</h3>
              <ul className="space-y-3">
                <li>
                  <a href="/about" className="text-gray-400 hover:text-indigo-400 transition">About</a>
                </li>
                <li>
                  <a href="/blog" className="text-gray-400 hover:text-indigo-400 transition">Blog</a>
                </li>
                <li>
                  <a href="/careers" className="text-gray-400 hover:text-indigo-400 transition">Careers</a>
                </li>
                <li>
                  <a href="/contact" className="text-gray-400 hover:text-indigo-400 transition">Contact</a>
                </li>
              </ul>
            </div> */}

            <div>
              <h3 className="text-lg font-semibold mb-4 text-white">Legal</h3>
              <ul className="space-y-3">
                <li>
                  <a href="/privacy" target="_blank" className="text-gray-400 hover:text-indigo-400 transition">Privacy Policy</a>
                </li>
                <li>
                  <a href="/terms-and-conditions" target="_blank" className="text-gray-400 hover:text-indigo-400 transition">Terms & Conditions</a>
                </li>
                <li>
                  <a href="/cookies" target="_blank" className="text-gray-400 hover:text-indigo-400 transition">Cookie Policy</a>
                </li>
                <li>
                  <a href="/data-processing" target="_blank" className="text-gray-400 hover:text-indigo-400 transition">Data Processing</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 text-gray-500">
              &copy; {new Date().getFullYear()} PrepBestie. All rights reserved.
            </div>
            {/* <div className="flex gap-4">
              <a href="/accessibility" className="text-gray-500 hover:text-indigo-400 transition text-sm">
                Accessibility
              </a>
              <span className="text-gray-700">•</span>
              <a href="/sitemap" className="text-gray-500 hover:text-indigo-400 transition text-sm">
                Sitemap
              </a>
              <span className="text-gray-700">•</span>
              <a href="/help-center" className="text-gray-500 hover:text-indigo-400 transition text-sm">
                Help Center
              </a>
            </div> */}
          </div>
        </div>
      </footer>

      {/* ------------------------- */}
      {/* BACK TO TOP BUTTON - Modernized */}
      {/* ------------------------- */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-30 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-500 transition focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
            aria-label="Back to top"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.9 }}
          >
            <FaArrowUp />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Pricing Calculator Modal */}
      <PricingCalculator 
        isOpen={showCalculator} 
        onClose={() => setShowCalculator(false)} 
      />
    </div>
  );
};

interface FaqItemProps {
  question: string;
  answer: string;
  delay: number;
}

const FaqItem: React.FC<FaqItemProps> = ({ question, answer, delay }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true, margin: "-100px" }}
      className="bg-white rounded-2xl p-1 shadow-lg overflow-hidden"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-5 cursor-pointer font-semibold text-lg bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-200 text-left"
      >
        <span>{question}</span>
        <motion.div
          className="w-8 h-8 bg-indigo-100 flex items-center justify-center rounded-full"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <FaAngleDown className="text-indigo-600" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-5 overflow-hidden"
          >
            <div className="py-5 pl-2 border-l-2 border-indigo-300 text-gray-600">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const faqItems = [
  {
    question: "How does PrepBestie work?",
    answer: "Our all-in-one platform combines AI-powered job analysis, CV generation, and simulator-friendly interview practice. We analyze job descriptions, generate tailored questions for verbal practice (no coding required), provide real-time feedback on your delivery, and track your progress with detailed analytics. The system learns from your interactions, becoming more personalized with each use."
  },
  {
    question: "Is my data secure?",
    answer: "Absolutely. We prioritize your privacy with top-notch security measures, ensuring your data is safe and confidential. We use industry-standard encryption protocols, secure servers, and regular security audits. Your practice sessions are only accessible to you, and we never share your data with third parties without your explicit consent."
  },
  {
    question: "Can I try the demo before signing up?",
    answer: "Yes, you can watch our demo video to see how our job analysis, CV generation, and interview practice features work together. We also offer a free trial period with limited features so you can experience our all-in-one platform, including progress tracking and simulator-friendly questions, before committing to a subscription."
  },
  {
    question: "What if I want to cancel my subscription?",
    answer: "You can cancel your subscription anytime. We offer flexible plans that adapt to your needs. There are no long-term contracts, and you can easily manage your subscription from your account settings. If you cancel, you'll continue to have access until the end of your current billing period."
  },
  {
    question: "Do you offer support if I need help?",
    answer: "Yes! We offer 24/7 customer support via chat, email, and phone. Our dedicated support team is always ready to assist you with any questions or issues you might encounter. We also have an extensive knowledge base and tutorial videos to help you get the most out of PrepBestie."
  }
];

export default LandingPage;
